import type { GalleryResource } from '@tadpoles/api-client'
import type { FamilyMedia } from '@tadpoles/common'

import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

type GalleryState = {
    readonly cursor?: string
    readonly familyMedia: ReadonlyArray<Readonly<FamilyMedia>>
    readonly phase: 'idle' | 'loading' | 'available' | 'failure'
}

export class GalleryService extends UserScopedService {
    readonly #api: GalleryResource
    readonly #operationController = new AbortController()
    #destroyed = false
    #loadPromise: Promise<void> | undefined
    #state = $state.raw<GalleryState>({ familyMedia: [], phase: 'idle' })

    public constructor(scope: UserScopedServiceScope, api: GalleryResource) {
        super(scope)
        this.#api = api
    }

    public get familyMedia(): GalleryState['familyMedia'] {
        return this.#state.familyMedia
    }

    public get phase(): GalleryState['phase'] {
        return this.#state.phase
    }

    public loadFirstPage(): Promise<void> {
        if (this.#destroyed) {
            throw new Error('Gallery service has been destroyed.')
        }

        if (this.#loadPromise !== undefined) {
            return this.#loadPromise
        }

        if (this.#state.phase === 'available') {
            return Promise.resolve()
        }

        this.#state = { ...this.#state, phase: 'loading' }
        const loadPromise = this.#load()
        this.#loadPromise = loadPromise
        const clearLoad = (): void => {
            if (this.#loadPromise === loadPromise) {
                this.#loadPromise = undefined
            }
        }
        void loadPromise.then(clearLoad, clearLoad)
        return loadPromise
    }

    public destroy(): void {
        if (this.#destroyed) {
            return
        }

        this.#destroyed = true
        this.#operationController.abort()
        this.#state = { familyMedia: [], phase: 'idle' }
    }

    async #load(): Promise<void> {
        try {
            const response = await this.#api.list({}, { signal: this.#operationController.signal })
            if (this.#destroyed) {
                return
            }

            this.#state = {
                cursor: response.cursor,
                familyMedia: response.familyMedia,
                phase: 'available'
            }
        } catch (error) {
            if (this.#destroyed) {
                return
            }

            this.#state = { ...this.#state, phase: 'failure' }
            throw error
        }
    }
}
